import { RequireAgent, useUser } from "@procurement-agents/auth";
import { Badge } from "@procurement-agents/ui";
import { ApiError, type AdminUser, type AdminUsers, type AuditEntry, type ExistingAccount, type ScopeApi, type UserStatus } from "@procurement-agents/scope-api";
import { useCallback, useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { ScopeApiProvider, useScopeApi } from "../api";
import { relativeTime } from "../labels";

const ROLE_LABEL: Record<string, string> = { business_user: "Business", procurement_user: "Procurement", sme: "SME", template_publisher: "Template publisher", agent_configurator: "Agent configurator", admin: "Admin" };
const AGENT_LABEL: Record<string, string> = { scope: "Scope generation", report: "Evaluation report generation" };
const STATUS_LABEL: Record<UserStatus, string> = { invited: "Invited", active: "Active", deactivated: "Deactivated" };
const STATUS_TONE: Record<UserStatus, "neutral" | "good" | "warn"> = { invited: "neutral", active: "good", deactivated: "warn" };
const ACTION_LABEL: Record<string, string> = {
  invite: "invited", grant_access: "given access", resend_invite: "invitation resent", update: "details changed", deactivate: "deactivated",
  reactivate: "reactivated", sign_out_everywhere: "signed out everywhere", remove: "removed",
};

/** Tenant administration (SPEC_SLICE4 section 9, CHANGE_user_management). The list is a list: it says who is here
 *  and what state they are in. Everything that changes a person happens on their own page, where there is room to
 *  say what each change does. */
export function AdminApp({ api }: { api?: ScopeApi }) {
  return (
    <RequireAgent id="scope">
      <ScopeApiProvider api={api}>
        <Routes>
          <Route path="/" element={<UsersPage />} />
          <Route path="users/:sub" element={<UserPage />} />
        </Routes>
      </ScopeApiProvider>
    </RequireAgent>
  );
}

// ---- the list ----------------------------------------------------------------------------------

function UsersPage() {
  const { api, toast } = useScopeApi();
  const me = useUser();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminUsers | null | undefined>(undefined);
  const [tab, setTab] = useState<"users" | "audit">("users");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"" | UserStatus>("");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.adminUsers()); } catch (e) { setData(null); if (e instanceof ApiError) toast(e.detail, "warn"); }
  }, [api, toast]);
  useEffect(() => { void load(); }, [load]);

  if (data === undefined) return <p className="muted">Loading…</p>;
  if (data === null) return <div className="page"><p className="muted">User administration is not available. The Management API may not be configured for this workspace.</p><Link to="/" className="btn quiet">Back</Link></div>;

  const needle = q.trim().toLowerCase();
  const users = data.users.filter((u) => (!needle || (u.email ?? "").toLowerCase().includes(needle) || (u.name ?? "").toLowerCase().includes(needle)) && (!status || u.status === status));
  const names = (ids: string[], labels: Record<string, string>) => ids.length ? ids.map((i) => labels[i] ?? i).join(", ") : "—";

  return (
    <div className="page admin">
      <Link to="/" className="back-link">← Workspace</Link>
      <div className="row between wrap">
        <h2>Users</h2>
        {data.invite_mode === "managed"
          ? <button className="btn primary" onClick={() => setInviting(true)}>Invite user</button>
          : <span className="muted small">Accounts for this workspace are created in your identity provider.</span>}
      </div>
      <p className="muted small">Open someone to change their roles, agents and access. Passwords and sign-in stay in Auth0; the workspace never sees them.</p>

      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={tab === "users"} className={tab === "users" ? "on" : ""} onClick={() => setTab("users")}>People</button>
        <button role="tab" aria-selected={tab === "audit"} className={tab === "audit" ? "on" : ""} onClick={() => setTab("audit")}>History</button>
      </div>

      {tab === "audit" ? <AuditTable /> : (
        <>
          <div className="row gap wrap filters">
            <input type="search" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search people" />
            <select value={status} onChange={(e) => setStatus(e.target.value as "" | UserStatus)} aria-label="Filter by status">
              <option value="">Any status</option>
              <option value="active">Active</option>
              <option value="invited">Invited</option>
              <option value="deactivated">Deactivated</option>
            </select>
          </div>

          <table className="table admin-table">
            <thead><tr><th>User</th><th>Status</th><th>Roles</th><th>Agents</th><th title="Nominated to finish a scope themselves rather than hand it to procurement">Completes scopes</th><th>Last sign-in</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.sub} data-testid={`user-${u.sub}`} className={`clickable${u.status === "deactivated" ? " dimmed" : ""}`} onClick={() => navigate(`/admin/users/${encodeURIComponent(u.sub)}`)}>
                  <td>
                    <Link to={`/admin/users/${encodeURIComponent(u.sub)}`} onClick={(e) => e.stopPropagation()}>{u.name ?? u.email ?? u.sub}</Link>
                    {u.sub === me?.sub && <span className="muted small"> · you</span>}
                    {u.name && u.email && <><br /><span className="muted small">{u.email}</span></>}
                  </td>
                  <td><Badge tone={STATUS_TONE[u.status]}>{STATUS_LABEL[u.status]}</Badge></td>
                  <td className="small">{names(u.roles, ROLE_LABEL)}</td>
                  <td className="small">{names(u.agents, AGENT_LABEL)}</td>
                  <td className="small">{u.can_complete_scope ? "Yes" : <span className="muted">—</span>}</td>
                  <td className="small muted">{u.status === "invited" ? `invited ${u.invited_at ? relativeTime(u.invited_at) : "—"}` : u.last_login ? relativeTime(u.last_login) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p className="muted">{data.users.length ? "Nobody matches that." : "No users carry this tenant yet."}</p>}
          <p className="muted small">{users.length} of {data.users.length} · <Badge tone="neutral">role changes apply at the person&apos;s next sign-in</Badge></p>
        </>
      )}

      {inviting && <InviteDrawer data={data} onClose={() => setInviting(false)} onDone={() => { setInviting(false); void load(); }} />}
    </div>
  );
}

// ---- one person --------------------------------------------------------------------------------

function UserPage() {
  const { api, toast } = useScopeApi();
  const me = useUser();
  const navigate = useNavigate();
  const sub = decodeURIComponent(useParams().sub ?? "");
  const [data, setData] = useState<AdminUsers | null | undefined>(undefined);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [history, setHistory] = useState<AuditEntry[]>([]);

  const load = useCallback(async () => {
    try {
      const page = await api.adminUsers();
      setData(page);
      setUser(page.users.find((u) => u.sub === sub) ?? null);
      setHistory((await api.adminAudit({ user: sub, limit: 20 })).entries);
    } catch (e) { setData(null); if (e instanceof ApiError) toast(e.detail, "warn"); }
  }, [api, sub, toast]);
  useEffect(() => { void load(); }, [load]);

  if (data === undefined) return <p className="muted">Loading…</p>;
  if (data === null || user === null) return <div className="page"><p className="muted">That person is not in this workspace.</p><Link to="/admin" className="btn quiet">Back to users</Link></div>;

  const isMe = user.sub === me?.sub;
  const run = async (what: string, job: () => Promise<unknown>, after?: () => void) => {
    setBusy(what);
    try {
      await job();
      if (after) after(); else await load();
    } catch (e) { toast(e instanceof ApiError ? e.detail : String(e), "warn"); } finally { setBusy(null); }
  };
  // Values the workspace does not know (an account edited in Auth0 by hand: "sme_user" for "sme") grant nothing, and the
  // server refuses a save that carries them. They are named below the chips and left out of the next change.
  const known = { roles: data.roles, agents: data.agents, sme_domains: data.sme_domains?.map((d) => d.id) } as Record<string, string[] | undefined>;
  const unknownOf = (kind: "roles" | "agents" | "sme_domains") => user[kind].filter((x) => known[kind] && !known[kind]!.includes(x));
  const toggle = (kind: "roles" | "agents" | "sme_domains", value: string, on: boolean) => {
    const kept = user[kind].filter((x) => !unknownOf(kind).includes(x));
    const next = on ? [...kept, value] : kept.filter((x) => x !== value);
    return run(kind, async () => setUser(await api.patchUser(user.sub, { [kind]: next })), () => {});
  };
  const unknownNote = (kind: "roles" | "agents" | "sme_domains") => {
    const odd = unknownOf(kind);
    return odd.length ? <p className="small warn-text" role="note">{odd.map((x) => `“${x}”`).join(", ")} {odd.length === 1 ? "is not a" : "are not"} {kind === "roles" ? "workspace role" : kind === "agents" ? "workspace agent" : "SME domain"}{odd.length === 1 ? "" : "s"} and grant{odd.length === 1 ? "s" : ""} nothing. The next change here removes {odd.length === 1 ? "it" : "them"}.</p> : null;
  };
  const chips = (kind: "roles" | "agents" | "sme_domains", options: Array<{ id: string; label: string }>) => (
    <span className="chip-set" role="group" aria-label={kind.replace("_", " ")}>
      {options.map((o) => {
        const on = user[kind].includes(o.id);
        const locked = kind === "roles" && o.id === "admin" && isMe && on;
        return (
          <label key={o.id} className={`chip toggle ${on ? "on" : ""}`} title={locked ? "You are the last administrator here; give someone else the admin role first" : undefined}>
            <input type="checkbox" checked={on} disabled={busy === kind || user.status === "deactivated" || locked} onChange={(e) => void toggle(kind, o.id, e.target.checked)} /> {o.label}
          </label>
        );
      })}
    </span>
  );

  return (
    <div className="page admin user-detail">
      <Link to="/admin" className="back-link">← Users</Link>
      <div className="row between wrap">
        <h2>{user.name ?? user.email ?? user.sub}</h2>
        <Badge tone={STATUS_TONE[user.status]}>{STATUS_LABEL[user.status]}</Badge>
      </div>
      <p className="muted small">
        {user.email}{isMe ? " · you" : ""}
        {user.status === "invited" && <> · invited {user.invited_at ? relativeTime(user.invited_at) : "—"}{user.invited_by ? ` by ${user.invited_by}` : ""}</>}
        {user.status === "active" && <> · last signed in {user.last_login ? relativeTime(user.last_login) : "—"}</>}
        {user.status === "deactivated" && <> · deactivated{user.deactivated_by ? ` by ${user.deactivated_by}` : ""}</>}
      </p>

      <Profile user={user} onSaved={setUser} />

      <section className="panel">
        <h3>Roles</h3>
        <p className="muted small">What they may do. A change applies at their next sign-in.</p>
        {chips("roles", data.roles.map((r) => ({ id: r, label: ROLE_LABEL[r] ?? r })))}
        {unknownNote("roles")}
      </section>

      <section className="panel">
        <h3>Agents</h3>
        <p className="muted small">Which tiles they see. With none they sign in and see &ldquo;not set up yet&rdquo;.</p>
        {chips("agents", data.agents.map((a) => ({ id: a, label: AGENT_LABEL[a] ?? a })))}
        {unknownNote("agents")}
      </section>

      <section className="panel">
        <h3>Completing scopes</h3>
        <p className="muted small">After the requisition, a nominated person chooses to finish the scope themselves or hand it to procurement, for the categories this workspace allows. Everyone else&apos;s request goes to procurement. It applies to requests they make after their next sign-in.</p>
        <label className={`chip toggle ${user.can_complete_scope ? "on" : ""}`}>
          <input type="checkbox" checked={!!user.can_complete_scope} disabled={busy === "can_complete_scope" || user.status === "deactivated"}
            onChange={(e) => { const on = e.target.checked; void run("can_complete_scope", async () => setUser(await api.patchUser(user.sub, { can_complete_scope: on })), () => {}); }} /> May complete scopes
        </label>
        {user.can_complete_scope && !user.roles.includes("business_user") && <p className="muted small">It only matters for someone with the business user role: procurement completes its own requests anyway.</p>}
      </section>

      <section className="panel">
        <h3>SME domains</h3>
        {data.sme_domains.length
          ? <>
              <p className="muted small">Which clarifications reach them.</p>
              {chips("sme_domains", data.sme_domains.map((d) => ({ id: d.id, label: d.name })))}
            </>
          : <p className="muted small">SME domains are set in the tenant configuration (<code>sme_domains</code>). With none defined, any SME may answer any section.</p>}
      </section>

      <section className="panel">
        <h3>Access</h3>
        <div className="row gap wrap">
          {user.status === "invited" && <button className="btn quiet" disabled={busy === "resend"} onClick={() => void run("resend", () => api.resendInvite(user.sub))}>Resend invitation</button>}
          {user.status !== "deactivated" && <button className="btn quiet" disabled={busy === "signout"} onClick={() => void run("signout", () => api.signOutEverywhere(user.sub))}>Sign out everywhere</button>}
          {user.status === "deactivated"
            ? <button className="btn" disabled={busy === "reactivate"} onClick={() => void run("reactivate", () => api.reactivateUser(user.sub))}>Reactivate</button>
            : <button className="btn" disabled={isMe || busy === "deactivate"} title={isMe ? "You cannot deactivate yourself" : undefined} onClick={() => {
                const reason = window.prompt(`Why is ${user.email ?? user.sub} being deactivated? They are signed out and refused straight away.`);
                if (reason !== null) void run("deactivate", () => api.deactivateUser(user.sub, reason));
              }}>Deactivate</button>}
          {user.status === "invited" && !isMe && (
            <button className="btn danger" disabled={busy === "remove"} onClick={() => {
              if (window.prompt(`This removes ${user.email} permanently. Type the address to confirm.`) === user.email) void run("remove", () => api.removeUser(user.sub), () => navigate("/admin"));
            }}>Remove</button>
          )}
        </div>
        <p className="muted small">Signing out everywhere ends their sessions; they sign in again as normal. Deactivating refuses them until someone reactivates them. Only an invitation nobody has accepted can be removed — anyone who has signed in has work attributed to them.</p>
      </section>

      <section className="panel">
        <h3>History</h3>
        {history.length ? <AuditRows entries={history} /> : <p className="muted small">Nothing has been changed here yet.</p>}
      </section>
    </div>
  );
}

function Profile({ user, onSaved }: { user: AdminUser; onSaved: (u: AdminUser) => void }) {
  const { api, toast } = useScopeApi();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.name ?? "");
  const [email, setEmail] = useState(user.email ?? "");
  const [saving, setSaving] = useState(false);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      onSaved(await api.patchUser(user.sub, { name: name.trim(), ...(email.trim() !== user.email ? { email: email.trim() } : {}) }));
      setEditing(false);
    } catch (err) { toast(err instanceof ApiError ? err.detail : String(err), "warn"); } finally { setSaving(false); }
  };
  if (!editing) {
    return (
      <section className="panel">
        <div className="row between wrap">
          <h3>Details</h3>
          <button className="link" onClick={() => { setName(user.name ?? ""); setEmail(user.email ?? ""); setEditing(true); }}>✎ Edit</button>
        </div>
        <p className="muted small">{user.name ?? "No name"} · {user.email}</p>
      </section>
    );
  }
  return (
    <form className="panel" onSubmit={(e) => void save(e)} aria-label="Details">
      <h3>Details</h3>
      <label>Name<input type="text" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label>Email<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
      <p className="muted small">Changing the address asks Auth0 to verify the new one before they can sign in with it.</p>
      <div className="row gap end">
        <button type="button" className="btn quiet" onClick={() => setEditing(false)}>Cancel</button>
        <button type="submit" className="btn primary" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </form>
  );
}

// ---- invitation and history --------------------------------------------------------------------

function InviteDrawer({ data, onClose, onDone }: { data: AdminUsers; onClose: () => void; onDone: () => void }) {
  const { api, toast } = useScopeApi();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roles, setRoles] = useState<string[]>(["business_user"]);
  const [agents, setAgents] = useState<string[]>(["scope"]);
  const [completes, setCompletes] = useState(false);   // CHANGE_handoff_v2
  const [sending, setSending] = useState(false);
  const [existing, setExisting] = useState<ExistingAccount[] | null>(null);
  const flip = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setExisting(null);
    try {
      await api.inviteUser({ email: email.trim(), name: name.trim() || null, roles, agents, can_complete_scope: completes });
      toast(`Invitation sent to ${email.trim()}`, "info");
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.detail : String(err), "warn");
      // The address may already sign in another way. Show that account rather than leaving them to guess.
      if (err instanceof ApiError && err.status === 409) await check(email.trim());
    } finally { setSending(false); }
  };

  /** Whether this address already signs in some other way. Asked as soon as it is typed, so the administrator
   *  sees the account that exists before sending an invitation that could only fail. */
  const check = async (value: string) => {
    if (!value.includes("@")) return;
    try { setExisting((await api.lookupEmail(value)).existing.filter((u) => !u.in_workspace)); } catch { /* the invitation will say so */ }
  };

  const grant = async (account: ExistingAccount) => {
    setSending(true);
    try {
      await api.grantAccess(account.sub, { roles, agents, can_complete_scope: completes });
      toast(`${account.email} can now sign in to this workspace`, "info");
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.detail : String(err), "warn");
    } finally { setSending(false); }
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <form className="drawer" onClick={(e) => e.stopPropagation()} onSubmit={(e) => void submit(e)} aria-label="Invite user">
        <h3>Invite someone to the workspace</h3>
        <label>Email<input type="email" required value={email} autoFocus
          onChange={(e) => { setEmail(e.target.value); setExisting(null); }}
          onBlur={() => void check(email.trim())} /></label>
        <label>Name<input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="optional" /></label>
        <fieldset><legend>Roles</legend>
          {data.roles.map((r) => <label key={r} className={`chip toggle ${roles.includes(r) ? "on" : ""}`}><input type="checkbox" checked={roles.includes(r)} onChange={() => flip(roles, setRoles, r)} /> {ROLE_LABEL[r] ?? r}</label>)}
        </fieldset>
        <fieldset><legend>Agents</legend>
          {data.agents.map((a) => <label key={a} className={`chip toggle ${agents.includes(a) ? "on" : ""}`}><input type="checkbox" checked={agents.includes(a)} onChange={() => flip(agents, setAgents, a)} /> {AGENT_LABEL[a] ?? a}</label>)}
        </fieldset>
        {agents.length === 0 && <p className="muted small">With no agent they sign in and see &ldquo;not set up yet&rdquo; until you give them one.</p>}
        {roles.includes("business_user") && <label className="row small"><input type="checkbox" checked={completes} onChange={(e) => setCompletes(e.target.checked)} /> May complete scopes themselves</label>}
        {existing?.length ? (
          <div className="notice" role="status">
            <strong>That address already has an account</strong>
            <p className="muted small">Auth0 keeps each way of signing in as its own account. Give the one they use access, with the roles and agents above, rather than creating a second.</p>
            {existing.map((u) => (
              <div key={u.sub} className="row between wrap">
                <span className="small">{u.email} · signs in with {u.connection}{u.elsewhere ? " · belongs to another workspace" : ""}</span>
                <button type="button" className="btn" disabled={sending || u.elsewhere} onClick={() => void grant(u)}>Give access</button>
              </div>
            ))}
          </div>
        ) : null}
        <p className="muted small">Auth0 emails them a link to set their password. You can send it again from their page.</p>
        <div className="row gap end">
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={sending || !email.trim()}>{sending ? "Sending…" : "Send invitation"}</button>
        </div>
      </form>
    </div>
  );
}

function AuditTable() {
  const { api, toast } = useScopeApi();
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  useEffect(() => {
    api.adminAudit({ limit: 100 }).then((p) => setEntries(p.entries)).catch((e) => { setEntries([]); if (e instanceof ApiError) toast(e.detail, "warn"); });
  }, [api, toast]);
  if (entries === null) return <p className="muted">Loading…</p>;
  if (!entries.length) return <p className="muted">Nothing has been changed here yet.</p>;
  return <AuditRows entries={entries} withTarget />;
}

function AuditRows({ entries, withTarget = false }: { entries: AuditEntry[]; withTarget?: boolean }) {
  return (
    <table className="table admin-table">
      <thead><tr><th>When</th><th>Who</th><th>What</th><th>Change</th></tr></thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td className="small muted">{relativeTime(e.ts)}</td>
            <td className="small">{e.actor_email ?? e.actor_sub}</td>
            <td className="small">{ACTION_LABEL[e.action] ?? e.action}{withTarget && <><br /><span className="muted">{e.target_email ?? e.target_sub}</span></>}</td>
            <td className="small muted">{describe(e)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** What actually changed, in the words of the row rather than as a JSON diff. */
function describe(e: AuditEntry): string {
  if (e.reason) return e.reason;
  if (!e.before || !e.after) return e.action === "invite" ? (e.after?.roles ?? []).map((r) => ROLE_LABEL[r] ?? r).join(", ") || "no roles" : "";
  const parts: string[] = [];
  for (const kind of ["roles", "agents", "sme_domains"] as const) {
    const from = e.before[kind].join(", ") || "none";
    const to = e.after[kind].join(", ") || "none";
    if (from !== to) parts.push(`${kind.replace("_", " ")}: ${from} → ${to}`);
  }
  if (!!e.before.can_complete_scope !== !!e.after.can_complete_scope) parts.push(e.after.can_complete_scope ? "may complete scopes" : "no longer completes scopes");
  if (e.before.email !== e.after.email) parts.push(`email: ${e.before.email} → ${e.after.email}`);
  if (e.before.name !== e.after.name) parts.push(`name: ${e.before.name ?? "—"} → ${e.after.name ?? "—"}`);
  return parts.join(" · ");
}
